/**
 * EditConfirmModal.tsx — Confirmation dialog for the click-to-edit affordance
 * on Phase 1 profile rows in P9_Reveal.
 *
 * Prevents accidental navigation: the user must confirm before jumping back
 * to the step that owns the data they want to change. Closes on Cancel or
 * backdrop click; on confirm, calls `onConfirm(targetStep)`.
 */

import React from 'react';
import { STAGE_TO_STEP, STAGE_EDIT_LABEL } from './constants';

interface EditConfirmModalProps {
  stageKey: string;
  onCancel: () => void;
  onConfirm: (targetStep: number) => void;
}

export const EditConfirmModal: React.FC<EditConfirmModalProps> = ({ stageKey, onCancel, onConfirm }) => {
  const target = STAGE_TO_STEP[stageKey];
  const label = STAGE_EDIT_LABEL[stageKey] || stageKey;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="p9-edit-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <h3 id="p9-edit-modal-title" className="text-base font-semibold text-gray-900">
          ¿Editar {label}?
        </h3>
        <p className="text-sm text-gray-600 mt-2">
          Saldrás del análisis y volverás a la pantalla de <strong>{label}</strong>. Cuando termines, vuelves aquí
          automáticamente.
        </p>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="px-3.5 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => {
              if (target !== undefined) onConfirm(target);
            }}
            className="px-3.5 py-2 rounded-lg bg-[#008BBC] text-white text-sm font-medium hover:bg-[#007aa8]"
          >
            Sí, editar
          </button>
        </div>
      </div>
    </div>
  );
};
